import { createSelector } from '@reduxjs/toolkit'
import _ from 'underscore'

import { getPackageSize, getRoutePriority, getTypePriority, groupByRoute, groupByType } from '../utils/serialTickets'

export const getUser = ({ user }) => user
export const getCompany = ({ company }) => company
export const getAgent = ({ agent }) => agent
export const getSerialTickets = ({ serialTickets }) => serialTickets.tickets
export const getSummary = ({ summary }) => summary
export const getAddedVouchers = ({ addedVouchers }) => addedVouchers
export const getReservation = ({ reservation }) => reservation
export const getDeliveryDetails = ({ deliveryDetails }) => deliveryDetails
export const isSsoErrorOccurred = ({ sso }) => sso.errorOccurred
export const isSsoDisabled = ({ sso }) => !sso.rendered || sso.errorOccurred
export const getPayment = ({ payment }) => payment
export const getActiveTab = ({ clientTypeTab }) => clientTypeTab.activeTab
export const getDigiData = ({ digiData }) => digiData
export const getCoupons = ({ coupons }) => coupons.coupons ?? []
export const getCouponsRequested = ({ coupons }) => coupons.requested

export const isCouponsLoading = ({ coupons }) => coupons.loading
export const getGroupedCoupons = createSelector(getCoupons, coupons => {
  const today = new Date()

  return _.chain(coupons)
    .groupBy(coupon => {
      if (coupon.used === 'Y') return 'used'
      return new Date(coupon.validUntil) < today ? 'expired' : 'unused'
    })
    .mapObject(group =>
      _.chain(group)
        .groupBy('couponClass')
        .mapObject(classGroup => {
          const sorted = _.sortBy(classGroup, c => new Date(c.validUntil))
          return _.groupBy(sorted, c => c.validUntil)
        })
        .value()
    )
    .value()
})

export const hasUsedCoupons = createSelector(getGroupedCoupons, groupedCoupons =>
  Object.values(groupedCoupons.used ?? {}).some(byDate => Object.values(byDate).some(coupons => coupons.length > 0))
)

export const hasExpiredCoupons = createSelector(getGroupedCoupons, grouped =>
  Object.values(grouped.expired ?? {}).some(byDate => Object.values(byDate).some(coupons => coupons.length > 0))
)

export const hasAvailableCoupons = createSelector(getGroupedCoupons, grouped =>
  Object.values(grouped.unused ?? {}).some(byDate => Object.values(byDate).some(coupons => coupons.length > 0))
)

export const getUserClientId = createSelector(getUser, user => user?.clientId)

export const isSerialTicketsLoading = ({ serialTickets }) => serialTickets.loading
export const getClubOneLevel = createSelector(getUser, user => user?.clubOneLevel)
export const isClubOneLogged = createSelector(getClubOneLevel, clubOneLevel => clubOneLevel !== 'NONE')
export const isCompanyCustomerLogged = createSelector(
  getCompany,
  company => company.valid && Object.keys(company.address).length > 0 && company.name.length > 0
)
export const isAgentLogged = createSelector(getAgent, agent => agent.valid && !!agent.agentName)

export const isDeliveryDetailsValid = createSelector(getDeliveryDetails, deliveryDetails => deliveryDetails?.valid)

export const getSelectedSerialTickets = createSelector(getSerialTickets, tickets =>
  tickets.filter(ticket => ticket.count > 0)
)

export const getSelectedSerialTicketsGroupedByRoute = createSelector(
  getSelectedSerialTickets,
  selectedSerialTickets => {
    const sortedTickets = _.chain(selectedSerialTickets)
      .sortBy(getTypePriority)
      .sortBy(getPackageSize)
      .sortBy(getRoutePriority)
      .value()
    return groupByRoute(sortedTickets)
  }
)

export const hasVouchers = createSelector(getAddedVouchers, addedVouchers => addedVouchers.length > 0)

export const hasSerialTickets = createSelector(getSerialTickets, serialTickets => serialTickets.length > 0)

export const hasSelectedTickets = createSelector(getSelectedSerialTickets, selectedTickets => !!selectedTickets.length)

export const getAvailablePaymentMethods = createSelector(getPayment, payment => payment.availableMethods)

export const isInvoicePaymentMethod = createSelector(getPayment, payment => payment.method === 'invoice')

export const isClubOneTabSelected = createSelector(getActiveTab, tab => tab === 'clubOne')
export const isCompanyTabSelected = createSelector(getActiveTab, tab => tab === 'company')

export const hasCompanyAgentLogin = createSelector(
  getCompany,
  isAgentLogged,
  (company, isAgentLogged) => company.hasAgent && isAgentLogged
)

export const hasVehicleAtReservation = createSelector(getReservation, ({ serialTickets }) =>
  serialTickets.some(({ type }) => type === 'VEHICLE')
)

export const getFirstValidVoucherId = createSelector(
  getReservation,
  hasVehicleAtReservation,
  ({ earnedCoupons }, hasVehicleAtReservation) =>
    earnedCoupons.find(({ valid }) => valid && !hasVehicleAtReservation)?.code
)

export const getSerialTicketsGroupedByRouteAndType = createSelector(getSerialTickets, tickets => {
  const sortedTickets = _.chain(tickets).sortBy(getTypePriority).sortBy(getPackageSize).sortBy(getRoutePriority).value()

  const routeGroups = groupByRoute(sortedTickets)
  return routeGroups.map(({ from, to, tickets }) => ({
    from,
    to,
    groups: groupByType(tickets)
  }))
})

export const getReservationSerialTicketsGroupedByRoute = createSelector(getReservation, reservation => {
  const sortedTickets = _.chain(reservation.serialTickets)
    .sortBy(getTypePriority)
    .sortBy(getPackageSize)
    .sortBy(getRoutePriority)
    .value()
  return groupByRoute(sortedTickets)
})

export const isFreeReservation = createSelector(
  getSummary,
  ({ price, regularPrice }) => price === 0 && regularPrice > 0
)

const getPaymentType = createSelector(getPayment, payment => {
  switch (payment.method) {
    case 'invoice':
      return 'invoice'
    case 'klarna':
      return 'klarna'
    case 'SWEDBANK':
      return 'SWEDBANK'
    case 'ADYEN':
      return 'ADYEN'
    default:
      throw new Error(`unsupported payment method "${payment.method}"`)
  }
})

export const getPaymentForConfirmReservation = createSelector(getPaymentType, paymentType => ({ type: paymentType }))
